/**
 * Custom hook for Integration Manager state management
 */

import { useCallback, useState } from 'react'
import { toast } from 'sonner'
import type { IntegrationFormData } from '../services/integration-service'
import { integrationService } from '../services/integration-service'
import { handleOAuth2Flow, isOAuth2Supported } from '../utils/oauth2-handler'

/**
 * Integration manager state
 */
interface IntegrationManagerState {
  isLoading: boolean
  isTesting: boolean
  errors: Record<string, string>
}

/**
 * Custom hook for integration manager state management
 */
export function useIntegrationManager() {
  const [state, setState] = useState<IntegrationManagerState>({
    isLoading: false,
    isTesting: false,
    errors: {}
  })

  /**
   * Save integration
   */
  const saveIntegration = useCallback(async (formData: IntegrationFormData) => {
    setState(prev => ({ ...prev, isLoading: true, errors: {} }))
    
    try {
      console.log('Saving integration:', {
        name: formData.name,
        provider: formData.provider
      })

      // Call the service to save the integration
      const result = await integrationService.saveIntegration(formData)
      
      if (!result.success) {
        setState(prev => ({ 
          ...prev, 
          isLoading: false,
          errors: { general: result.error }
        }))
        toast.error(result.error)
        return result
      }

      setState(prev => ({ 
        ...prev, 
        isLoading: false,
        errors: {}
      }))

      // Show success message
      const message = result.data.authorizeUrl 
        ? 'Integration saved successfully. Please complete OAuth authorization.'
        : 'Integration saved successfully!'
        
      toast.success(message)
      
      // Handle OAuth authorization if needed
      if (result.data.authorizeUrl) {
        if (!isOAuth2Supported()) {
          toast.error('OAuth2 authorization is not supported by your browser')
          setState(prev => ({ 
            ...prev, 
            isLoading: false,
            errors: { general: 'OAuth2 authorization requires popup support' }
          }))
          
          return {
            success: false,
            error: 'OAuth2 authorization is not supported by your browser'
          }
        }
        
        toast.success('Opening authorization window...')
        
        // Use our enhanced OAuth2 handler
        const oauth2Result = await handleOAuth2Flow({
          authorizationUrl: result.data.authorizeUrl,
          onSuccess: (data) => {
            toast.success('Integration authorized successfully!')
            console.log('OAuth2 success:', data)
          },
          onError: (error) => {
            toast.error(`Authorization failed: ${error.message}`)
            console.error('OAuth2 error:', error)
          },
          onCancel: () => {
            toast.warning('Authorization was cancelled')
          }
        })
        
        setState(prev => ({ ...prev, isLoading: false }))
        
        return {
          success: oauth2Result.success,
          data: oauth2Result.data,
          error: oauth2Result.error?.message ?? 'OAuth2 authorization failed'
        }
      }
      
      return result

    } catch (error) {
      console.error('Failed to save integration:', error)
      const errorMessage = error instanceof Error ? error.message : 'Failed to save integration'
      
      setState(prev => ({ 
        ...prev, 
        isLoading: false,
        errors: { general: errorMessage }
      }))
      
      toast.error(errorMessage)
      
      return {
        success: false,
        error: errorMessage
      }
    }
  }, [])

  /**
   * Test connection
   */
  const testConnection = useCallback(async (formData: IntegrationFormData): Promise<{ success: boolean; message: string }> => {
    setState(prev => ({ ...prev, isTesting: true, errors: {} }))
    
    try {
      console.log('Testing connection:', {
        name: formData.name,
        provider: formData.provider
      })

      // Call the service to test the connection
      const result = await integrationService.testConnection(formData)
      
      setState(prev => ({ 
        ...prev, 
        isTesting: false
      }))

      // Show result message
      if (result.success) {
        toast.success(result.message)
      } else {
        toast.error(result.message)
        setState(prev => ({ 
          ...prev, 
          errors: { connection: result.message }
        }))
      }
      
      return result

    } catch (error) {
      console.error('Failed to test connection:', error)
      const errorMessage = error instanceof Error ? error.message : 'Failed to test connection'
      
      setState(prev => ({ 
        ...prev, 
        isTesting: false,
        errors: { connection: errorMessage }
      }))
      
      toast.error(errorMessage)
      
      return {
        success: false,
        message: errorMessage
      }
    }
  }, [])

  /**
   * Reset errors
   */
  const resetErrors = useCallback(() => {
    setState(prev => ({ ...prev, errors: {} }))
  }, [])

  return {
    state,
    saveIntegration,
    testConnection,
    resetErrors
  }
}